import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { api, type EngagementIndexRow } from '../lib/api';
import {
  SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge, Spinner, EmptyState,
} from '../components/ui';

// The way into the engagement workspace. The workspace needs an engagement to
// be about, so the nav points here rather than at a bare route — a nav item
// leading nowhere useful is how the first cut of this shipped undiscoverable.
//
// Deliberately money-free: this is gated on the workspace key alone, and being
// allowed to open one engagement is not the same as being allowed to see
// ceilings and burn across every client at once.

export default function AdminEngagementsIndexPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery(() => api.engagementWorkspace.list(), []);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);

  const rows = useMemo(() => {
    const all = data?.engagements ?? [];
    const needle = search.trim().toLowerCase();
    return all.filter((e) => {
      if (activeOnly && e.status === 'Completed') return false;
      if (!needle) return true;
      return e.jira_issue_key.toLowerCase().includes(needle)
        || (e.summary ?? '').toLowerCase().includes(needle)
        || (e.tempo_account_name ?? '').toLowerCase().includes(needle)
        || (e.tempo_account_id ?? '').toLowerCase().includes(needle)
        || (e.delivery_manager_name ?? '').toLowerCase().includes(needle);
    });
  }, [data, search, activeOnly]);

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;

  const open = (e: EngagementIndexRow) => navigate(`/admin/engagements/${encodeURIComponent(e.jira_issue_id)}/w/overview`);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Engagements</SectionLabel>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          Open an engagement to see what it is and how it is performing
        </span>
      </div>

      <Card>
        <CardHeader>
          <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 11, color: 'var(--color-text-muted)' }}>
            <input type="checkbox" checked={activeOnly} onChange={(ev) => setActiveOnly(ev.target.checked)} />
            Active only
          </label>
          <span style={{ marginLeft: 'auto' }}>
            <input
              type="search"
              placeholder="Filter by key, client, summary, DM…"
              value={search}
              onChange={(ev) => setSearch(ev.target.value)}
              autoFocus
              style={{
                fontSize: 11, padding: '4px 8px', width: 300,
                border: '0.5px solid var(--color-border-strong)',
                borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
                color: 'var(--color-text)',
              }}
            />
          </span>
        </CardHeader>

        {rows.length === 0 ? (
          <EmptyState message={search.trim() ? 'No matches.' : activeOnly ? 'No active engagements. Untick “Active only” for completed ones.' : 'No engagements.'} />
        ) : (
          <div style={{ maxHeight: 'calc(100vh - 240px)', overflow: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th>Engagement</Th>
                  <Th>Client</Th>
                  <Th>Type</Th>
                  <Th>Delivery manager</Th>
                  <Th right>Deliverables</Th>
                  <Th>Last worked</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <Tr key={e.jira_issue_id} onClick={() => open(e)}>
                    <Td bold>
                      <span style={{ color: 'var(--color-blue)', marginRight: 6 }}>{e.jira_issue_key}</span>
                      <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{e.summary}</span>
                    </Td>
                    <Td style={{ fontSize: 11 }}>{e.tempo_account_name ?? '—'}</Td>
                    <Td>{e.engagement_type ? <Badge variant="blue">{e.engagement_type}</Badge> : <span style={{ color: 'var(--color-text-hint)' }}>—</span>}</Td>
                    <Td muted style={{ fontSize: 11 }}>{e.delivery_manager_name ?? '—'}</Td>
                    <Td right muted style={{ fontSize: 11 }}>{e.deliverable_count}</Td>
                    <Td muted style={{ fontSize: 11 }}>{e.latest_work_date ?? '—'}</Td>
                    <Td><Badge variant={e.status === 'Completed' ? 'green' : 'gray'}>{e.status ?? '—'}</Badge></Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </>
  );
}
